import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, LogOut, ImagePlus, FileText, Paperclip, Check, CalendarClock, CalendarDays, Search, Activity, Pencil, Sparkles, Flame, Settings, Target } from "lucide-react";
import Attachment from "@/components/Attachment";
import { IDP_FIELDS } from "@/lib/idpFields";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const TOKEN_KEY = "jg_admin_token";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("gallery");

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/admin/verify`, authHeader)
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      })
      .finally(() => setChecking(false));
  }, []); // eslint-disable-line

  const login = async () => {
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setAuthed(true);
      toast.success("Welcome back");
    } catch {
      toast.error("Incorrect password");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 grain relative" data-testid="admin-login">
        <div className="max-w-sm w-full relative z-10">
          <span className="text-[#2E6BE6] text-xs uppercase tracking-[0.3em]">Admin</span>
          <h1 className="font-display font-bold uppercase tracking-tighter text-4xl mt-3 text-white">JG<span className="text-[#2E6BE6]">/</span>Coaching</h1>
          <p className="text-white/50 mt-3 text-sm">Enter your password to manage the site.</p>
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            placeholder="Password"
            className="w-full bg-transparent border-b border-white/20 py-3 mt-8 outline-none focus:border-[#D4AF37] transition-colors placeholder:text-white/25 text-white"
          />
          <button
            data-testid="admin-login-btn"
            onClick={login}
            disabled={loggingIn || !password}
            className="mt-6 w-full bg-[#D4AF37] text-black py-4 text-sm uppercase tracking-[0.15em] font-semibold flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-40"
          >
            {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter"}
          </button>
          <Link to="/" className="block mt-6 text-center text-white/40 text-xs uppercase tracking-[0.2em] link-underline">Back to site</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 md:px-10 h-20 flex items-center justify-between">
        <span className="font-display font-bold uppercase tracking-tight text-xl">JG<span className="text-[#2E6BE6]">/</span>Coaching — Admin</span>
        <div className="flex items-center gap-6">
          <Link to="/" className="text-xs uppercase tracking-[0.2em] text-white/60 link-underline hover:text-white">View site</Link>
          <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex gap-2 mb-10 border-b border-white/10">
          {[["gallery", "Gallery"], ["bookings", "Bookings"], ["clients", "Client Lookup"], ["engagement", "Engagement"], ["reports", "Session Reports"], ["idp", "IDP Plans"], ["reschedules", "Reschedules"], ["settings", "Settings"]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => setTab(id)}
              className={`px-5 py-3 text-xs uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${
                tab === id ? "border-[#D4AF37] text-white" : "border-transparent text-white/40 hover:text-white/70"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "gallery" ? <GalleryPanel token={token} authHeader={authHeader} />
          : tab === "bookings" ? <BookingsPanel authHeader={authHeader} />
          : tab === "clients" ? <LookupPanel authHeader={authHeader} />
          : tab === "engagement" ? <EngagementPanel authHeader={authHeader} />
          : tab === "reports" ? <ReportsPanel token={token} authHeader={authHeader} />
          : tab === "idp" ? <IdpPanel authHeader={authHeader} />
          : tab === "reschedules" ? <ReschedulePanel authHeader={authHeader} />
          : <SettingsPanel authHeader={authHeader} />}
      </div>
    </div>
  );
}

/* ---------- Gallery panel ---------- */
function GalleryPanel({ token, authHeader }) {
  const [images, setImages] = useState([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => axios.get(`${API}/gallery`).then((r) => setImages(r.data.images || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("caption", caption);
        await axios.post(`${API}/gallery/upload`, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded`);
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch {
      toast.error("Upload failed. Try a JPG, PNG or WEBP.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`, authHeader);
      setImages((p) => p.filter((i) => i.id !== id));
      toast.success("Photo removed");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="gallery-panel">
      <div className="border border-white/10 p-6 md:p-8 mb-12">
        <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-5">Upload photos</p>
        <div className="flex flex-col md:flex-row gap-4 md:items-end">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-[0.15em] text-white/40">Caption (optional)</label>
            <input data-testid="upload-caption" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Sprint drills"
              className="w-full bg-transparent border-b border-white/20 py-2 mt-2 outline-none focus:border-[#D4AF37] transition-colors placeholder:text-white/25" />
          </div>
          <input ref={fileRef} data-testid="upload-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
          <button data-testid="upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="bg-[#D4AF37] text-black px-7 py-3.5 text-sm uppercase tracking-[0.15em] font-semibold flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-50">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Choose files</>}
          </button>
        </div>
        <p className="text-white/30 text-xs mt-4">JPG, PNG, WEBP or GIF. You can select multiple at once.</p>
      </div>

      <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-5">
        Live gallery ({images.length}) {images.length === 0 && "— stock photos show on the site until you upload"}
      </p>
      {images.length === 0 ? (
        <div className="border border-dashed border-white/15 py-20 flex flex-col items-center text-white/40" data-testid="gallery-empty">
          <ImagePlus className="w-10 h-10 mb-4" />
          <p className="text-sm">No photos yet. Upload your first above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-gallery-grid">
          {images.map((img) => (
            <div key={img.id} className="group relative aspect-square overflow-hidden border border-white/10" data-testid={`admin-img-${img.id}`}>
              <img src={`${BACKEND}${img.url}`} alt={img.caption || "photo"} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button data-testid={`delete-${img.id}`} onClick={() => remove(img.id)}
                  className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-red-600 transition-colors">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
              {img.caption && <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-[0.15em] text-white/80 bg-black/50 px-2 py-1">{img.caption}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Reports panel ---------- */
function ReportsPanel({ token, authHeader }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [reports, setReports] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [focus, setFocus] = useState("");
  const [rating, setRating] = useState("");
  const [attachFile, setAttachFile] = useState(null);
  const attachRef = useRef(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/clients`, authHeader).then((r) => setClients(r.data.clients || [])).catch(() => {});
  }, []); // eslint-disable-line

  const loadReports = (cid) => {
    if (!cid) { setReports([]); return; }
    axios.get(`${API}/admin/reports`, { ...authHeader, params: { client_id: cid } })
      .then((r) => setReports(r.data.reports || [])).catch(() => {});
  };

  useEffect(() => { loadReports(clientId); }, [clientId]); // eslint-disable-line

  const save = async () => {
    if (!clientId) { toast.error("Pick a client first"); return; }
    if (!notes.trim()) { toast.error("Add some notes"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("client_id", clientId);
      fd.append("date", date);
      fd.append("notes", notes.trim());
      fd.append("focus_areas", focus);
      fd.append("rating", rating);
      if (attachFile) fd.append("file", attachFile);
      await axios.post(`${API}/admin/reports`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast.success("Report saved — client emailed");
      setNotes(""); setFocus(""); setRating(""); setAttachFile(null);
      if (attachRef.current) attachRef.current.value = "";
      loadReports(clientId);
    } catch {
      toast.error("Could not save report");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/reports/${id}`, authHeader);
      setReports((p) => p.filter((r) => r.id !== id));
      toast.success("Report deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const inputCls = "w-full bg-transparent border-b border-white/20 py-2 mt-2 outline-none focus:border-[#D4AF37] transition-colors placeholder:text-white/25 text-white";

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="reports-panel">
      {/* Form */}
      <div className="lg:col-span-5">
        <div className="border border-white/10 p-6 md:p-8">
          <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-5">New session report</p>
          <label className="text-xs uppercase tracking-[0.15em] text-white/40">Client</label>
          <select data-testid="report-client-select" value={clientId} onChange={(e) => setClientId(e.target.value)}
            className={`${inputCls} appearance-none cursor-pointer`}>
            <option value="" className="bg-[#141414]">Select a client…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id} className="bg-[#141414]">{c.name} — {c.email}</option>
            ))}
          </select>
          {clients.length === 0 && <p className="text-white/30 text-xs mt-2">No clients yet. They appear here once someone signs up.</p>}

          <div className="mt-5">
            <label className="text-xs uppercase tracking-[0.15em] text-white/40">Session date</label>
            <input data-testid="report-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${inputCls} [color-scheme:dark]`} />
          </div>
          <div className="mt-5">
            <label className="text-xs uppercase tracking-[0.15em] text-white/40">Focus areas (comma separated)</label>
            <input data-testid="report-focus" value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Passing, Fitness, Finishing" className={inputCls} />
          </div>
          <div className="mt-5">
            <label className="text-xs uppercase tracking-[0.15em] text-white/40">Rating (1–10, optional)</label>
            <input data-testid="report-rating" type="number" min="1" max="10" value={rating} onChange={(e) => setRating(e.target.value)} placeholder="8" className={inputCls} />
          </div>
          <div className="mt-5">
            <label className="text-xs uppercase tracking-[0.15em] text-white/40">Notes</label>
            <textarea data-testid="report-notes" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="How did the session go? What to work on next?"
              className={`${inputCls} resize-none`} />
          </div>
          <div className="mt-5">
            <label className="text-xs uppercase tracking-[0.15em] text-white/40">Attach photo / video (optional)</label>
            <input ref={attachRef} data-testid="report-attach" type="file" accept="image/*,video/mp4,video/quicktime,video/webm"
              onChange={(e) => setAttachFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-white/60 mt-2 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#D4AF37] file:text-black file:text-xs file:uppercase file:tracking-wider file:font-semibold file:cursor-pointer" />
            {attachFile && <span className="text-white/40 text-xs mt-1 block">{attachFile.name}</span>}
          </div>
          <button data-testid="report-save" onClick={save} disabled={saving}
            className="mt-6 w-full bg-[#D4AF37] text-black py-4 text-sm uppercase tracking-[0.15em] font-semibold flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-50">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save & notify client"}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="lg:col-span-7">
        <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-5">
          {clientId ? `Reports (${reports.length})` : "Select a client to see their reports"}
        </p>
        {clientId && reports.length === 0 && (
          <div className="border border-dashed border-white/15 py-16 flex flex-col items-center text-white/40" data-testid="admin-reports-empty">
            <FileText className="w-8 h-8 mb-3" />
            <p className="text-sm">No reports for this client yet.</p>
          </div>
        )}
        <div className="space-y-4">
          {reports.map((r) => (
            <div key={r.id} className="border border-white/10 p-5" data-testid={`admin-report-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="text-xs uppercase tracking-[0.2em] text-white/40">{r.date}</span>
                  {typeof r.rating === "number" && <span className="ml-3 text-[#D4AF37] font-display font-bold">{r.rating}/10</span>}
                </div>
                <button data-testid={`admin-report-delete-${r.id}`} onClick={() => remove(r.id)} className="text-white/40 hover:text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {r.focus_areas?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {r.focus_areas.map((f, i) => <span key={i} className="text-[10px] uppercase tracking-[0.15em] border border-white/15 px-2 py-1 text-white/70">{f}</span>)}
                </div>
              )}
              <p className="text-white/70 mt-3 whitespace-pre-wrap text-sm leading-relaxed">{r.notes}</p>
              {r.has_attachment && (
                <>
                  <p className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-white/40 mt-4">
                    <Paperclip className="w-3.5 h-3.5" /> Attachment
                  </p>
                  <Attachment reportId={r.id} type={r.attachment_type} token={token} />
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ---------- Bookings panel ---------- */
function BookingsPanel({ authHeader }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("upcoming"); // upcoming | all | incomplete
  const [today, setToday] = useState("");

  useEffect(() => {
    setLoading(true);
    const url = view === "incomplete"
      ? `${API}/admin/bookings/incomplete`
      : `${API}/admin/bookings/upcoming`;
    const params = view === "incomplete" ? {} : { include_past: view === "all" };
    axios.get(url, { ...authHeader, params })
      .then((r) => { setBookings(r.data.bookings || []); setToday(r.data.today || ""); })
      .catch(() => toast.error("Could not load bookings"))
      .finally(() => setLoading(false));
  }, [view]); // eslint-disable-line

  const dayLabel = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" });
  };

  // group by date (for upcoming/all views)
  const groups = bookings.reduce((acc, b) => {
    (acc[b.date] = acc[b.date] || []).push(b);
    return acc;
  }, {});
  const dates = Object.keys(groups).sort();

  const filters = [["upcoming", "Upcoming"], ["all", "All (incl. past)"], ["incomplete", "Unpaid / incomplete"]];
  const heading = view === "incomplete" ? "Incomplete bookings" : view === "all" ? "All bookings" : "Upcoming bookings";

  return (
    <div data-testid="bookings-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <p className="text-xs uppercase tracking-[0.25em] text-white/40">{heading} ({bookings.length})</p>
        <div className="flex border border-white/15">
          {filters.map(([id, label]) => (
            <button
              key={id}
              data-testid={`bookings-filter-${id}`}
              onClick={() => setView(id)}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.15em] transition-colors ${view === id ? "bg-[#D4AF37] text-black" : "text-white/60 hover:text-white"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === "incomplete" && bookings.length > 0 && (
        <p className="text-white/40 text-xs mb-5 -mt-2">These players started a booking but their payment never completed — good follow-up leads. Sorted newest first.</p>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-white/50"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : bookings.length === 0 ? (
        <div className="border border-dashed border-white/15 py-16 flex flex-col items-center text-white/40" data-testid="bookings-empty">
          <CalendarDays className="w-8 h-8 mb-3" />
          <p className="text-sm">
            {view === "incomplete" ? "No incomplete bookings — everyone paid up." : view === "all" ? "No confirmed bookings found." : "No upcoming confirmed bookings yet."}
          </p>
          {view === "upcoming" && <p className="text-xs mt-2 text-white/30">Made bookings for past dates? Tap “All (incl. past)” above.</p>}
        </div>
      ) : view === "incomplete" ? (
        <div className="space-y-2">
          {bookings.map((b) => (
            <div key={b.id} className="border border-white/10 p-4 flex items-center justify-between gap-4" data-testid={`booking-${b.id}`}>
              <div className="flex items-center gap-4">
                <span className="font-display font-bold text-base tabular-nums w-28">{b.date} {b.time}</span>
                <div>
                  <p className="text-white font-medium">{b.name}</p>
                  <p className="text-white/50 text-sm">{b.email}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="text-[10px] uppercase tracking-[0.15em] border border-yellow-400/40 text-yellow-400 px-2 py-1 capitalize">{b.payment_status}</span>
                <span className="text-[10px] uppercase tracking-[0.15em] text-white/50 capitalize">{b.session_type} · {b.plan}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-8">
          {dates.map((d) => (
            <div key={d} data-testid={`bookings-day-${d}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className={`font-display font-bold text-lg ${today && d < today ? "text-white/40" : "text-[#D4AF37]"}`}>{dayLabel(d)}</span>
                <span className="text-white/40 text-xs">{groups[d].length} session{groups[d].length > 1 ? "s" : ""}</span>
                {today && d < today && <span className="text-[9px] uppercase tracking-[0.15em] text-white/30 border border-white/15 px-2 py-0.5">Past</span>}
              </div>
              <div className="space-y-2">
                {groups[d].map((b) => (
                  <div key={b.id} className="border border-white/10 p-4 flex items-center justify-between gap-4" data-testid={`booking-${b.id}`}>
                    <div className="flex items-center gap-4">
                      <span className="font-display font-bold text-xl tabular-nums w-16">{b.time}</span>
                      <div>
                        <p className="text-white font-medium">{b.name}</p>
                        <p className="text-white/50 text-sm">{b.email}</p>
                        {b.notes && <p className="text-white/40 text-sm mt-1">“{b.notes}”</p>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-[10px] uppercase tracking-[0.15em] border border-[#D4AF37]/50 text-[#D4AF37] px-2 py-1 capitalize">{b.session_type}</span>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-white/60 capitalize">{b.plan}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ---------- Client Lookup panel ---------- */
function LookupPanel({ authHeader }) {
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");

  const search = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) { toast.error("Type at least 2 characters"); return; }
    setLoading(true); setSearched(true);
    try {
      const { data } = await axios.get(`${API}/admin/lookup`, { ...authHeader, params: { q: q.trim() } });
      setResult(data);
    } catch {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const statusStyle = (s) => s === "paid"
    ? "border-green-400/40 text-green-400"
    : "border-yellow-400/40 text-yellow-400";

  const doRename = async (email) => {
    if (!renameVal.trim()) { toast.error("Enter a name"); return; }
    try {
      await axios.put(`${API}/admin/bookings/rename`, { email, name: renameVal.trim() }, authHeader);
      toast.success("Name updated across their bookings");
      setRenaming(null); setRenameVal("");
      search();
    } catch {
      toast.error("Could not rename");
    }
  };

  return (
    <div data-testid="lookup-panel">
      <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">Find a client by name or email</p>
      <form onSubmit={search} className="flex gap-3 max-w-xl mb-8">
        <input
          data-testid="lookup-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="e.g. carolmcquitty@gmail.com or Larry"
          className="flex-1 bg-transparent border-b border-white/20 py-2 outline-none focus:border-[#D4AF37] transition-colors placeholder:text-white/25 text-white"
        />
        <button data-testid="lookup-search-btn" type="submit" disabled={loading}
          className="bg-[#D4AF37] text-black px-5 py-2 text-xs uppercase tracking-[0.15em] font-semibold flex items-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-40">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
        </button>
      </form>

      {searched && !loading && result && (
        <div className="space-y-8" data-testid="lookup-results">
          {/* Account */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">Account</p>
            {result.account ? (
              <div className="border border-white/10 p-4">
                <p className="text-white font-medium">{result.account.name || "—"}</p>
                <p className="text-white/50 text-sm">{result.account.email}</p>
              </div>
            ) : (
              <p className="text-white/40 text-sm">No registered account with this name/email (they may have booked as a guest).</p>
            )}
          </div>

          {/* Rename player (updates their bookings) */}
          {result.bookings.length > 0 && (
            <div data-testid="rename-panel">
              <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">Rename player</p>
              <p className="text-white/40 text-xs mb-3">If a parent signed up under their own name, set the player's name here — it updates across all their bookings.</p>
              <div className="space-y-2">
                {[...new Set(result.bookings.map((b) => b.email))].map((email) => {
                  const current = result.bookings.find((b) => b.email === email)?.name;
                  return (
                    <div key={email} className="flex items-center gap-3 flex-wrap border border-white/10 p-3">
                      <span className="text-white/70 text-sm flex-1 min-w-[180px]">{current} — {email}</span>
                      {renaming === email ? (
                        <>
                          <input data-testid="rename-input" value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                            placeholder="Player's name"
                            className="bg-transparent border-b border-white/20 py-1.5 text-sm outline-none focus:border-[#D4AF37] text-white placeholder:text-white/25 min-w-[160px]" />
                          <button data-testid="rename-save" onClick={() => doRename(email)}
                            className="bg-[#D4AF37] text-black px-4 py-1.5 text-xs uppercase tracking-[0.12em] font-semibold">Save</button>
                          <button onClick={() => setRenaming(null)} className="text-white/50 hover:text-white text-xs uppercase tracking-[0.12em]">Cancel</button>
                        </>
                      ) : (
                        <button data-testid={`rename-btn-${email}`} onClick={() => { setRenaming(email); setRenameVal(current || ""); }}
                          className="flex items-center gap-1.5 border border-white/20 text-white/70 px-3 py-1.5 text-xs uppercase tracking-[0.12em] hover:border-white/50 transition-colors">
                          <Pencil className="w-3.5 h-3.5" /> Edit name
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bookings */}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">Bookings ({result.bookings.length})</p>
            {result.bookings.length === 0 ? (
              <p className="text-white/40 text-sm" data-testid="lookup-no-bookings">No bookings found.</p>
            ) : (
              <div className="space-y-2">
                {result.bookings.map((b) => (
                  <div key={b.id} className="border border-white/10 p-4 flex items-center justify-between gap-4" data-testid={`lookup-booking-${b.id}`}>
                    <div>
                      <p className="text-white font-medium">{b.date} · {b.time}</p>
                      <p className="text-white/50 text-sm">{b.name} — {b.email}</p>
                      {b.notes && <p className="text-white/40 text-sm mt-1">“{b.notes}”</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-1 capitalize ${statusStyle(b.payment_status)}`}>{b.payment_status}</span>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-white/50 capitalize">{b.session_type} · {b.plan}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Reschedules */}
          {result.reschedules.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-3">Reschedule requests ({result.reschedules.length})</p>
              <div className="space-y-2">
                {result.reschedules.map((r) => (
                  <div key={r.id} className="border border-white/10 p-4 flex items-center justify-between gap-4">
                    <p className="text-white/70 text-sm">
                      {r.original_date ? <span className="line-through text-white/40">{r.original_date} · {r.original_time}</span> : null}
                      {r.original_date ? " → " : ""}{r.preferred_date} · {r.preferred_time}
                    </p>
                    <span className="text-[10px] uppercase tracking-[0.15em] border border-white/20 text-white/60 px-2 py-1 capitalize">{r.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ---------- Engagement panel (weekly challenge drafts) ---------- */
function EngagementPanel({ authHeader }) {
  const [drafts, setDrafts] = useState([]);
  const [sent, setSent] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genId, setGenId] = useState("");
  const [busy, setBusy] = useState(null);
  const [edit, setEdit] = useState({}); // id -> {title, description}
  const [library, setLibrary] = useState([]);
  const [newLib, setNewLib] = useState({ title: "", description: "" });
  const [badgeQueue, setBadgeQueue] = useState([]);
  const [badgeSent, setBadgeSent] = useState([]);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/challenges?status=draft`, authHeader),
      axios.get(`${API}/admin/challenges?status=sent`, authHeader),
      axios.get(`${API}/admin/clients`, authHeader),
      axios.get(`${API}/admin/challenge-library`, authHeader),
      axios.get(`${API}/admin/badge-notifications`, authHeader),
    ]).then(([d, s, c, l, b]) => {
      setDrafts(d.data.challenges || []);
      setSent((s.data.challenges || []).slice(0, 10));
      setClients(c.data.clients || []);
      setLibrary(l.data.items || []);
      setBadgeQueue(b.data.pending || []);
      setBadgeSent(b.data.sent || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const addLib = async () => {
    if (!newLib.title.trim()) { toast.error("Add a title"); return; }
    try { await axios.post(`${API}/admin/challenge-library`, newLib, authHeader); toast.success("Saved to library"); setNewLib({ title: "", description: "" }); load(); }
    catch { toast.error("Could not save"); }
  };
  const delLib = async (id) => {
    try { await axios.delete(`${API}/admin/challenge-library/${id}`, authHeader); setLibrary((p) => p.filter((x) => x.id !== id)); }
    catch { toast.error("Could not delete"); }
  };
  const applyLib = async (item) => {
    if (!genId) { toast.error("Pick a player first (top selector)"); return; }
    setBusy(`lib-${item.id}`);
    try { await axios.post(`${API}/admin/challenges/from-library`, { client_id: genId, title: item.title, description: item.description }, authHeader); toast.success("Added to the approval queue for that player"); load(); }
    catch { toast.error("Could not add"); }
    finally { setBusy(null); }
  };
  const saveDraftToLib = async (c) => {
    try { await axios.post(`${API}/admin/challenge-library`, { title: c.title, description: c.description }, authHeader); toast.success("Saved to library"); load(); }
    catch { toast.error("Could not save"); }
  };
  const sendDigests = async () => {
    setBusy("digest");
    try { const { data } = await axios.post(`${API}/admin/digest/send-now`, {}, authHeader); toast.success(`Monthly recap emailed to ${data.sent} player(s)`); }
    catch { toast.error("Could not send digests"); }
    finally { setBusy(null); }
  };

  const sendBadge = async (id) => {
    setBusy(`badge-${id}`);
    try { await axios.post(`${API}/admin/badge-notifications/${id}/send`, {}, authHeader); toast.success("Badge congratulations emailed to the parent 🏅"); load(); }
    catch { toast.error("Could not send"); }
    finally { setBusy(null); }
  };
  const dismissBadge = async (id) => {
    try { await axios.delete(`${API}/admin/badge-notifications/${id}`, authHeader); setBadgeQueue((p) => p.filter((x) => x.id !== id)); }
    catch { toast.error("Could not dismiss"); }
  };

  const generate = async () => {
    if (!genId) { toast.error("Pick a player"); return; }
    setBusy("gen");
    try {
      await axios.post(`${API}/admin/challenges/generate/${genId}`, {}, authHeader);
      toast.success("Challenge drafted — review & approve below");
      load();
    } catch { toast.error("Couldn't draft right now"); }
    finally { setBusy(null); }
  };

  const saveEdit = async (id) => {
    const e = edit[id]; if (!e) return;
    try { await axios.put(`${API}/admin/challenges/${id}`, { title: e.title, description: e.description }, authHeader); toast.success("Saved"); setEdit((p) => { const n = { ...p }; delete n[id]; return n; }); load(); }
    catch { toast.error("Could not save"); }
  };
  const approve = async (id) => {
    setBusy(id);
    try { await axios.post(`${API}/admin/challenges/${id}/approve`, {}, authHeader); toast.success("Approved & emailed to the player 🔥"); load(); }
    catch { toast.error("Could not approve"); }
    finally { setBusy(null); }
  };
  const del = async (id) => {
    try { await axios.delete(`${API}/admin/challenges/${id}`, authHeader); setDrafts((p) => p.filter((c) => c.id !== id)); }
    catch { toast.error("Could not delete"); }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/50"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div data-testid="engagement-panel">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/40 mb-2"><Sparkles className="w-4 h-4 text-[#2E6BE6]" /> Weekly skill challenges</p>
      <p className="text-white/40 text-xs mb-6">AI drafts a personalised challenge for each player every Monday (7am) — rotating through their weakest skill-wheel categories with a fresh game format each week, so no two feel the same. Review, edit and approve — nothing sends until you approve. You can also draft one on demand below.</p>

      <div className="flex flex-wrap items-end gap-3 mb-8 border border-white/10 p-4">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1">Draft a challenge for</label>
          <select data-testid="engagement-gen-select" value={genId} onChange={(e) => setGenId(e.target.value)}
            className="bg-transparent border-b border-white/20 py-2 text-white outline-none focus:border-[#D4AF37] min-w-[240px] cursor-pointer">
            <option value="" className="bg-[#141414]">Select a player…</option>
            {clients.map((c) => <option key={c.id} value={c.id} className="bg-[#141414]">{c.name} — {c.email}</option>)}
          </select>
        </div>
        <button data-testid="engagement-generate" onClick={generate} disabled={busy === "gen"}
          className="bg-[#2E6BE6] text-white px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold flex items-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-50">
          {busy === "gen" ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting…</> : <><Sparkles className="w-4 h-4" /> Draft challenge</>}
        </button>
      </div>

      <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">Awaiting your approval ({drafts.length})</p>
      {drafts.length === 0 ? (
        <p className="text-white/40 text-sm mb-8" data-testid="engagement-no-drafts">No drafts waiting. Draft one above or wait for Monday's batch.</p>
      ) : (
        <div className="space-y-4 mb-10">
          {drafts.map((c) => {
            const e = edit[c.id];
            return (
              <div key={c.id} className="border border-[#D4AF37]/30 p-4" data-testid={`challenge-draft-${c.id}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="text-[#D4AF37] text-xs uppercase tracking-[0.15em]">{c.client_name} · week of {c.week_start}</span>
                  <div className="flex gap-2">
                    {e
                      ? <button data-testid={`challenge-save-${c.id}`} onClick={() => saveEdit(c.id)} className="text-xs uppercase tracking-[0.12em] text-[#2E6BE6]">Save edit</button>
                      : <button data-testid={`challenge-edit-${c.id}`} onClick={() => setEdit((p) => ({ ...p, [c.id]: { title: c.title, description: c.description } }))} className="flex items-center gap-1 text-xs uppercase tracking-[0.12em] text-white/50 hover:text-white"><Pencil className="w-3.5 h-3.5" /> Edit</button>}
                  </div>
                </div>
                {e ? (
                  <div className="space-y-2">
                    <input value={e.title} onChange={(ev) => setEdit((p) => ({ ...p, [c.id]: { ...e, title: ev.target.value } }))}
                      className="w-full bg-transparent border border-white/15 p-2 text-white text-sm outline-none focus:border-[#D4AF37]" />
                    <textarea rows={3} value={e.description} onChange={(ev) => setEdit((p) => ({ ...p, [c.id]: { ...e, description: ev.target.value } }))}
                      className="w-full bg-transparent border border-white/15 p-2 text-white text-sm outline-none focus:border-[#D4AF37] resize-none" />
                  </div>
                ) : (
                  <>
                    <p className="font-display font-bold text-lg text-white">{c.title}</p>
                    <p className="text-white/70 text-sm mt-1">{c.description}</p>
                  </>
                )}
                <div className="flex gap-2 mt-4">
                  <button data-testid={`challenge-approve-${c.id}`} onClick={() => approve(c.id)} disabled={busy === c.id}
                    className="bg-[#D4AF37] text-black px-5 py-2 text-xs uppercase tracking-[0.15em] font-semibold flex items-center gap-2 disabled:opacity-50">
                    {busy === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve & send
                  </button>
                  <button data-testid={`challenge-delete-${c.id}`} onClick={() => del(c.id)} className="border border-white/15 text-white/50 px-4 py-2 text-xs uppercase tracking-[0.15em] hover:text-red-400 hover:border-red-400/40">Discard</button>
                  <button data-testid={`challenge-tolib-${c.id}`} onClick={() => saveDraftToLib(c)} className="border border-white/15 text-white/50 px-4 py-2 text-xs uppercase tracking-[0.15em] hover:text-[#2E6BE6] hover:border-[#2E6BE6]/40">Save to library</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {sent.length > 0 && (
        <>
          <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">Recently sent</p>
          <div className="space-y-2">
            {sent.map((c) => (
              <div key={c.id} className="border border-white/10 p-3 flex items-center justify-between gap-3 text-sm" data-testid={`challenge-sent-${c.id}`}>
                <span className="text-white/70">{c.client_name} · {c.title}</span>
                <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${c.completed ? "border-green-400/40 text-green-400" : "border-white/15 text-white/40"}`}>{c.completed ? "Completed" : "Sent"}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Badge celebrations */}
      <div className="mt-12 border-t border-white/10 pt-8" data-testid="badge-notifications">
        <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-2">Badge celebrations</p>
        <p className="text-white/40 text-xs mb-5">When a player hits a streak milestone (4, 8 or 12 weeks), a congratulations email queues here. Nothing is sent until you approve — the parent gets it, with a copy to you. Each badge emails once ever.</p>
        {badgeQueue.length === 0 ? (
          <p className="text-white/40 text-sm" data-testid="badge-empty">No badges waiting. They'll appear here the moment a player unlocks one.</p>
        ) : (
          <div className="space-y-3">
            {badgeQueue.map((b) => (
              <div key={b.id} className="border border-[#D4AF37]/30 p-4 flex items-center justify-between gap-3" data-testid={`badge-pending-${b.id}`}>
                <div>
                  <p className="font-display font-bold text-white">{b.client_name} <span className="text-[#D4AF37]">· {b.label}</span></p>
                  <p className="text-white/50 text-xs mt-1">{b.weeks}-week streak · {b.client_email}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`badge-send-${b.id}`} onClick={() => sendBadge(b.id)} disabled={busy === `badge-${b.id}`}
                    className="bg-[#D4AF37] text-black px-4 py-2 text-xs uppercase tracking-[0.12em] font-semibold flex items-center gap-2 disabled:opacity-50">
                    {busy === `badge-${b.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Send congratulations
                  </button>
                  <button data-testid={`badge-dismiss-${b.id}`} onClick={() => dismissBadge(b.id)}
                    className="border border-white/15 text-white/50 px-4 py-2 text-xs uppercase tracking-[0.15em] hover:text-red-400 hover:border-red-400/40">Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {badgeSent.length > 0 && (
          <div className="mt-5 space-y-2">
            {badgeSent.map((b) => (
              <div key={b.id} className="border border-white/10 p-3 flex items-center justify-between gap-3 text-sm" data-testid={`badge-sent-${b.id}`}>
                <span className="text-white/70">{b.client_name} · {b.label}</span>
                <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-green-400/40 text-green-400">Emailed</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Challenge library */}
      <div className="mt-12 border-t border-white/10 pt-8" data-testid="challenge-library">
        <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-2">Challenge library</p>
        <p className="text-white/40 text-xs mb-5">Save favourites to reuse. Pick a player in the selector above, then "Use" to drop it into their approval queue.</p>
        <div className="border border-white/10 p-4 mb-5 space-y-2">
          <input data-testid="library-title-input" value={newLib.title} onChange={(e) => setNewLib((p) => ({ ...p, title: e.target.value }))}
            placeholder="Challenge title" className="w-full bg-transparent border border-white/15 p-2 text-white text-sm outline-none focus:border-[#D4AF37]" />
          <textarea data-testid="library-desc-input" rows={2} value={newLib.description} onChange={(e) => setNewLib((p) => ({ ...p, description: e.target.value }))}
            placeholder="Description / instructions" className="w-full bg-transparent border border-white/15 p-2 text-white text-sm outline-none focus:border-[#D4AF37] resize-none" />
          <button data-testid="library-add" onClick={addLib} className="bg-[#2E6BE6] text-white px-5 py-2 text-xs uppercase tracking-[0.15em] font-semibold">Save to library</button>
        </div>
        {library.length === 0 ? (
          <p className="text-white/40 text-sm" data-testid="library-empty">No saved challenges yet.</p>
        ) : (
          <div className="space-y-2">
            {library.map((item) => (
              <div key={item.id} className="border border-white/10 p-4 flex items-start justify-between gap-3" data-testid={`library-item-${item.id}`}>
                <div className="flex-1">
                  <p className="font-display font-bold text-white">{item.title}</p>
                  <p className="text-white/60 text-sm mt-1">{item.description}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`library-use-${item.id}`} onClick={() => applyLib(item)} disabled={busy === `lib-${item.id}`}
                    className="bg-[#D4AF37] text-black px-4 py-2 text-xs uppercase tracking-[0.12em] font-semibold disabled:opacity-50">Use</button>
                  <button data-testid={`library-del-${item.id}`} onClick={() => delLib(item.id)} className="text-white/30 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Monthly parent digest */}
      <div className="mt-12 border-t border-white/10 pt-8" data-testid="digest-section">
        <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-2">Parent monthly recap</p>
        <p className="text-white/40 text-xs mb-4">Parents automatically get a monthly recap (streak, challenges done, skill-wheel progress) on the 1st. You can also send this month's recap to everyone now.</p>
        <button data-testid="digest-send-now" onClick={sendDigests} disabled={busy === "digest"}
          className="border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold flex items-center gap-2 hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50">
          {busy === "digest" ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send monthly recaps now"}
        </button>
      </div>
    </div>
  );
}


/* ---------- Reschedule panel ---------- */
function ReschedulePanel({ authHeader }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    axios.get(`${API}/admin/reschedule`, authHeader)
      .then((r) => setRequests(r.data.requests || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/reschedule/${id}`, { status }, authHeader);
      setRequests((p) => p.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(status === "confirmed" ? "Confirmed — session moved & client emailed" : `Marked ${status}`);
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(e.response.data?.detail || "That slot is no longer free.");
      } else {
        toast.error("Could not update");
      }
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/reschedule/${id}`, authHeader);
      setRequests((p) => p.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const badge = {
    pending: "text-yellow-400 border-yellow-400/40",
    confirmed: "text-green-400 border-green-400/40",
    declined: "text-red-400 border-red-400/40",
  };

  if (loading) return <div className="flex items-center gap-2 text-white/50"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div data-testid="reschedule-panel">
      <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-5">Reschedule requests ({requests.length})</p>
      {requests.length === 0 ? (
        <div className="border border-dashed border-white/15 py-16 flex flex-col items-center text-white/40" data-testid="reschedule-empty">
          <CalendarClock className="w-8 h-8 mb-3" />
          <p className="text-sm">No reschedule requests yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <div key={r.id} className="border border-white/10 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid={`admin-reschedule-${r.id}`}>
              <div>
                <div className="flex items-center gap-3">
                  {r.original_date && (
                    <span className="text-white/40 text-sm line-through">{r.original_date} · {r.original_time}</span>
                  )}
                  {r.original_date && <span className="text-[#D4AF37]">→</span>}
                  <span className="font-display font-bold text-lg">{r.preferred_date} · {r.preferred_time}</span>
                  <span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-1 ${badge[r.status] || "text-white/50 border-white/20"}`}>{r.status}</span>
                </div>
                <p className="text-white/50 text-sm mt-1">{r.client_name} — {r.client_email}</p>
                {r.note && <p className="text-white/40 text-sm mt-1">“{r.note}”</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.status !== "confirmed" && (
                  <button data-testid={`reschedule-confirm-${r.id}`} onClick={() => setStatus(r.id, "confirmed")}
                    className="flex items-center gap-1 bg-[#D4AF37] text-black px-3 py-2 text-xs uppercase tracking-[0.12em] font-semibold hover:-translate-y-0.5 transition-transform">
                    <Check className="w-3.5 h-3.5" /> Confirm
                  </button>
                )}
                {r.status !== "declined" && (
                  <button data-testid={`reschedule-decline-${r.id}`} onClick={() => setStatus(r.id, "declined")}
                    className="border border-white/20 text-white/70 px-3 py-2 text-xs uppercase tracking-[0.12em] hover:border-white/50 transition-colors">
                    Decline
                  </button>
                )}
                <button data-testid={`reschedule-delete-${r.id}`} onClick={() => remove(r.id)} className="text-white/40 hover:text-red-400 transition-colors p-2">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Settings panel ---------- */
function SettingsPanel({ authHeader }) {
  const [months, setMonths] = useState(2);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then((r) => setMonths(r.data.booking_window_months || 2))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    const m = Math.max(1, Math.min(24, Number(months) || 1));
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/settings`, { booking_window_months: m }, authHeader);
      setMonths(m);
      toast.success("Booking window updated");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/50"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="max-w-lg" data-testid="settings-panel">
      <div className="border border-white/10 p-6 md:p-8">
        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/40 mb-5">
          <Settings className="w-4 h-4" /> Booking window
        </p>
        <label className="text-xs uppercase tracking-[0.15em] text-white/40">How far ahead can clients book? (months)</label>
        <input
          data-testid="settings-months"
          type="number"
          min="1"
          max="24"
          value={months}
          onChange={(e) => setMonths(e.target.value)}
          className="w-full bg-transparent border-b border-white/20 py-2 mt-2 outline-none focus:border-[#D4AF37] transition-colors text-white text-2xl font-display font-bold"
        />
        <p className="text-white/40 text-sm mt-3">
          Clients can only pick dates up to <span className="text-[#D4AF37] font-semibold">{months} month{Number(months) === 1 ? "" : "s"}</span> from today. Dates beyond that are disabled on the calendar.
        </p>
        <button
          data-testid="settings-save"
          onClick={save}
          disabled={saving}
          className="mt-6 bg-[#D4AF37] text-black px-7 py-3.5 text-sm uppercase tracking-[0.15em] font-semibold flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-50"
        >
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save"}
        </button>
      </div>
    </div>
  );
}


/* ---------- IDP panel ---------- */
const emptyIdp = () => ({ objective: "", strengths: "", development_areas: "", technical: "", physical: "", tactical: "", mental: "", short_term_goals: "", long_term_goals: "", review_date: "" });

function IdpPanel({ authHeader }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [idp, setIdp] = useState(emptyIdp());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/clients`, authHeader).then((r) => setClients(r.data.clients || [])).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!clientId) { setIdp(emptyIdp()); setPendingDraft(null); return; }
    setLoading(true);
    axios.get(`${API}/admin/idp/${clientId}`, authHeader)
      .then((r) => setIdp({ ...emptyIdp(), ...(r.data.idp || {}) }))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.get(`${API}/admin/idp-drafts`, authHeader)
      .then((r) => setPendingDraft((r.data.drafts || []).find((d) => d.client_id === clientId) || null))
      .catch(() => {});
  }, [clientId]); // eslint-disable-line

  const genDraft = async () => {
    setDrafting(true);
    try {
      const { data } = await axios.post(`${API}/admin/idp/generate/${clientId}`, {}, authHeader);
      setIdp((p) => ({ ...p, ...data.draft }));
      toast.success("Draft ready — review, edit anything, then Save & notify");
    } catch {
      toast.error("Couldn't draft right now — please try again");
    } finally {
      setDrafting(false);
    }
  };

  const loadPending = async () => {
    if (!pendingDraft) return;
    setIdp((p) => ({ ...p, ...pendingDraft.draft }));
    toast.success("Draft loaded — review and Save to confirm");
  };
  const discardPending = async () => {
    if (!pendingDraft) return;
    try { await axios.delete(`${API}/admin/idp-drafts/${pendingDraft.id}`, authHeader); setPendingDraft(null); toast.success("Draft dismissed"); }
    catch { toast.error("Could not dismiss"); }
  };

  const set = (k, v) => setIdp((p) => ({ ...p, [k]: v }));

  const downloadPdf = async () => {
    if (!clientId) { toast.error("Pick a client first"); return; }
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/player-report/${clientId}/pdf`, { ...authHeader, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.href = url;
      a.download = m ? m[1] : "JG-Coaching-Report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded — ready to print");
    } catch {
      toast.error("Could not build the PDF right now");
    } finally {
      setDownloading(false);
    }
  };

  const save = async () => {
    if (!clientId) { toast.error("Pick a client first"); return; }
    setSaving(true);
    try {
      const payload = {}; IDP_FIELDS.forEach((f) => (payload[f.key] = idp[f.key] || ""));
      payload.review_date = idp.review_date || "";
      await axios.put(`${API}/admin/idp/${clientId}`, payload, authHeader);
      if (pendingDraft) {
        axios.delete(`${API}/admin/idp-drafts/${pendingDraft.id}`, authHeader).catch(() => {});
        setPendingDraft(null);
      }
      toast.success("Development plan saved — client notified");
    } catch {
      toast.error("Could not save plan");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-white/15 p-3 mt-2 outline-none focus:border-[#D4AF37] transition-colors resize-none placeholder:text-white/25 text-white text-sm";

  return (
    <div data-testid="idp-panel">
      <div className="max-w-md">
        <label className="text-xs uppercase tracking-[0.15em] text-white/40">Player</label>
        <select data-testid="idp-client-select" value={clientId} onChange={(e) => setClientId(e.target.value)}
          className="w-full bg-transparent border-b border-white/20 py-2 mt-2 outline-none focus:border-[#D4AF37] text-white appearance-none cursor-pointer">
          <option value="" className="bg-[#141414]">Select a player…</option>
          {clients.map((c) => <option key={c.id} value={c.id} className="bg-[#141414]">{c.name} — {c.email}</option>)}
        </select>
        {clients.length === 0 && <p className="text-white/30 text-xs mt-2">No clients yet.</p>}
      </div>

      {clientId && (
        <div className="mt-8">
          {loading ? (
            <div className="flex items-center gap-2 text-white/50"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
                <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/40">
                  <Target className="w-4 h-4" /> Individual Development Plan
                </p>
                <button data-testid="idp-ai-draft" onClick={genDraft} disabled={drafting}
                  className="flex items-center gap-2 border border-[#2E6BE6]/50 text-[#2E6BE6] px-4 py-2 text-[11px] uppercase tracking-[0.15em] hover:bg-[#2E6BE6]/10 transition-colors disabled:opacity-50">
                  {drafting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Drafting…</> : <><Sparkles className="w-3.5 h-3.5" /> Draft for me</>}
                </button>
              </div>
              {pendingDraft && (
                <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 mb-5 flex items-center justify-between gap-3 flex-wrap" data-testid="idp-pending-draft">
                  <p className="text-sm text-white/80">A quarterly review draft is ready for this player.</p>
                  <div className="flex gap-2">
                    <button data-testid="idp-pending-load" onClick={loadPending} className="bg-[#D4AF37] text-black px-4 py-2 text-[11px] uppercase tracking-[0.12em] font-semibold">Review draft</button>
                    <button data-testid="idp-pending-discard" onClick={discardPending} className="border border-white/20 text-white/60 px-4 py-2 text-[11px] uppercase tracking-[0.12em] hover:text-white">Dismiss</button>
                  </div>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-5">
                {IDP_FIELDS.map((f) => (
                  <div key={f.key} className={f.rows >= 3 ? "md:col-span-1" : "md:col-span-1"}>
                    <label className="text-xs uppercase tracking-[0.15em] text-white/40">{f.label}</label>
                    <textarea data-testid={`idp-${f.key}`} rows={f.rows} value={idp[f.key] || ""}
                      onChange={(e) => set(f.key, e.target.value)} className={inputCls} />
                  </div>
                ))}
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-white/40">Next review date</label>
                  <input data-testid="idp-review_date" type="date" value={idp.review_date || ""}
                    onChange={(e) => set("review_date", e.target.value)} className={`${inputCls} [color-scheme:dark]`} />
                </div>
              </div>
              <button data-testid="idp-save" onClick={save} disabled={saving}
                className="mt-6 bg-[#D4AF37] text-black px-7 py-3.5 text-sm uppercase tracking-[0.15em] font-semibold flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-50">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save & notify player"}
              </button>
              <button data-testid="idp-download-pdf" onClick={downloadPdf} disabled={downloading}
                className="mt-3 border border-[#2E6BE6]/60 text-[#2E6BE6] px-7 py-3.5 text-sm uppercase tracking-[0.15em] font-semibold flex items-center justify-center gap-2 hover:bg-[#2E6BE6]/10 transition-colors disabled:opacity-50">
                {downloading ? <><Loader2 className="w-4 h-4 animate-spin" /> Building PDF…</> : "Download printable report (PDF)"}
              </button>
              <p className="text-white/30 text-xs mt-2">Dated visual report — skill wheel radar, strengths &amp; focus charts, progress graphs, fitness results and the development plan. Print and hand to the player.</p>

              <SkillsEditor clientId={clientId} authHeader={authHeader} />
              <FitnessEditor clientId={clientId} authHeader={authHeader} />
            </>
          )}
        </div>
      )}
    </div>
  );
}


/* ---------- Skills editor (admin) — rate 500+ skills, grouped & searchable ---------- */
function SkillsEditor({ clientId, authHeader }) {
  const [all, setAll] = useState([]); // [{name, category, rating}]
  const [categories, setCategories] = useState([]);
  const [maxRating, setMaxRating] = useState(7);
  const [activeCat, setActiveCat] = useState("");
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [newSkill, setNewSkill] = useState("");
  const [newCat, setNewCat] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [suggested, setSuggested] = useState(new Set());

  const load = () => {
    Promise.all([
      axios.get(`${API}/skills-catalog`),
      axios.get(`${API}/admin/skills/${clientId}`, authHeader),
    ]).then(([cat, rat]) => {
      const ratings = rat.data.ratings || {};
      const cats = cat.data.categories || [];
      setCategories(cats);
      setMaxRating(cat.data.max_rating || 7);
      setActiveCat((prev) => prev || cats[0] || "");
      setAll((cat.data.catalog || []).map((c) => ({ ...c, rating: ratings[c.name] || 0 })));
    }).catch(() => {});
  };
  useEffect(() => { if (clientId) load(); }, [clientId]); // eslint-disable-line

  const update = (name, key, val) => setAll((p) => p.map((s) => (s.name === name ? { ...s, [key]: val } : s)));
  const removeSkill = (name) => setAll((p) => p.filter((s) => s.name !== name));

  const q = query.trim().toLowerCase();
  const visible = q
    ? all.filter((s) => s.name.toLowerCase().includes(q))
    : all.filter((s) => s.category === activeCat);

  const catCount = (c) => all.filter((s) => s.category === c).length;

  const save = async () => {
    setSaving(true);
    try {
      const catalog = all.filter((s) => s.name.trim()).map((s) => ({ name: s.name.trim(), category: s.category }));
      const ratings = {};
      all.forEach((s) => { if (s.name.trim()) ratings[s.name.trim()] = Number(s.rating) || 0; });
      await axios.put(`${API}/admin/skills-catalog`, { catalog, categories }, authHeader);
      await axios.put(`${API}/admin/skills/${clientId}`, { ratings }, authHeader);
      toast.success("Skills saved — player's wheel updated");
      setSuggested(new Set());
      load();
    } catch {
      toast.error("Could not save skills");
    } finally {
      setSaving(false);
    }
  };

  const addSkill = () => {
    const name = newSkill.trim();
    if (!name) return;
    if (all.some((s) => s.name.toLowerCase() === name.toLowerCase())) { toast.error("That skill already exists"); return; }
    setAll((p) => [...p, { name, category: activeCat || categories[0], rating: 0 }]);
    setNewSkill("");
  };
  const addCategory = () => {
    const c = newCat.trim();
    if (!c || categories.includes(c)) { setNewCat(""); return; }
    setCategories((p) => [...p, c]);
    setActiveCat(c);
    setNewCat("");
  };

  const aiSuggest = async () => {
    if (!activeCat || aiBusy) return;
    setAiBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/skills/${clientId}/ai-rate`, { category: activeCat }, authHeader);
      const sug = data.suggestions || {};
      const names = Object.keys(sug);
      if (!names.length) { toast.info(data.summary || "This section is already fully rated."); return; }
      setAll((p) => p.map((s) => (sug[s.name] !== undefined && !Number(s.rating) ? { ...s, rating: sug[s.name] } : s)));
      setSuggested((p) => { const n = new Set(p); names.forEach((x) => n.add(x)); return n; });
      toast.success(`AI suggested ${names.length} rating(s) in ${activeCat} — tweak anything, then Save ratings`);
      if (data.summary) toast.info(data.summary, { duration: 7000 });
    } catch {
      toast.error("Couldn't get AI suggestions right now");
    } finally {
      setAiBusy(false);
    }
  };

  const aiSuggestAll = async () => {
    if (aiBusy) return;
    setAiBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/skills/${clientId}/ai-rate-all`, {}, authHeader);
      const sug = data.suggestions || {};
      const names = Object.keys(sug);
      if (!names.length) { toast.info("Every skill on the wheel is already rated."); return; }
      setAll((p) => p.map((s) => (sug[s.name] !== undefined && !Number(s.rating) ? { ...s, rating: sug[s.name] } : s)));
      setSuggested(new Set(names));
      toast.success(`AI evaluated the entire wheel — ${names.length} ratings suggested. Review each section, tweak, then Save ratings`);
    } catch {
      toast.error("Couldn't evaluate the wheel right now");
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="mt-12 border-t border-white/10 pt-10" data-testid="skills-editor">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/40 mb-2">
        <Target className="w-4 h-4" /> Skill Wheel ratings
      </p>
      <p className="text-white/40 text-xs mb-5">Pick a category (or search), set this player's 0–{maxRating} rating per skill. The shared skill list &amp; categories apply to everyone; ratings are per player.</p>

      <input data-testid="skill-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder="Search all skills…"
        className="w-full max-w-sm bg-transparent border-b border-white/20 py-2 mb-5 outline-none focus:border-[#D4AF37] transition-colors placeholder:text-white/25 text-white text-sm" />

      {!q && (
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((c) => (
            <button key={c} data-testid={`skill-cat-tab-${c}`} onClick={() => setActiveCat(c)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] border transition-colors ${activeCat === c ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60 hover:border-white/40"}`}>
              {c} <span className="opacity-50">{catCount(c)}</span>
            </button>
          ))}
          <div className="flex items-center gap-1">
            <input data-testid="new-category-input" value={newCat} onChange={(e) => setNewCat(e.target.value)}
              placeholder="+ category" className="w-24 bg-transparent border-b border-white/20 py-1 text-xs outline-none focus:border-[#D4AF37] text-white placeholder:text-white/25" />
            <button data-testid="add-category-btn" onClick={addCategory} className="text-white/50 hover:text-white text-xs">Add</button>
          </div>
        </div>
      )}

      {!q && activeCat && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <button data-testid="skill-ai-suggest" onClick={aiSuggest} disabled={aiBusy}
            className="border border-[#2E6BE6]/60 text-[#2E6BE6] px-4 py-2 text-[11px] uppercase tracking-[0.12em] font-semibold flex items-center gap-2 hover:bg-[#2E6BE6]/10 transition-colors disabled:opacity-50">
            {aiBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analysing…</> : <><Sparkles className="w-3.5 h-3.5" /> AI suggest — {activeCat}</>}
          </button>
          <button data-testid="skill-ai-suggest-all" onClick={aiSuggestAll} disabled={aiBusy}
            className="border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2 text-[11px] uppercase tracking-[0.12em] font-semibold flex items-center gap-2 hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50">
            {aiBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Evaluating…</> : <><Sparkles className="w-3.5 h-3.5" /> AI evaluate entire wheel</>}
          </button>
          <span className="text-white/30 text-[11px]">Fills only unrated skills, anchored to your existing ratings + session notes. You review before saving.</span>
        </div>
      )}

      <div className="space-y-2 max-h-[520px] overflow-y-auto pr-2">
        {visible.map((s) => (
          <div key={s.name} className="flex items-center gap-3 flex-wrap" data-testid={`skill-row-${s.name}`}>
            <span className="flex-1 min-w-[160px] text-sm text-white/85">{s.name}
              {suggested.has(s.name) && <span data-testid={`skill-ai-chip-${s.name}`} className="ml-2 text-[9px] uppercase tracking-[0.1em] text-[#2E6BE6] border border-[#2E6BE6]/40 px-1.5 py-0.5 align-middle">AI pick</span>}
            </span>
            <select value={s.category} onChange={(e) => update(s.name, "category", e.target.value)}
              data-testid={`skill-cat-select-${s.name}`}
              className="bg-transparent border-b border-white/20 py-1 text-white/70 text-[11px] uppercase tracking-wider outline-none focus:border-[#D4AF37] cursor-pointer">
              {categories.map((c) => <option key={c} value={c} className="bg-[#141414]">{c}</option>)}
            </select>
            <div className="flex items-center gap-1">
              {Array.from({ length: maxRating + 1 }, (_, n) => (
                <button key={n} data-testid={`skill-${s.name}-rate-${n}`} onClick={() => update(s.name, "rating", n)}
                  className={`w-7 h-7 text-[11px] border transition-all ${Number(s.rating) === n ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/50 hover:border-white/40"}`}>
                  {n}
                </button>
              ))}
            </div>
            <button data-testid={`skill-remove-${s.name}`} onClick={() => removeSkill(s.name)} className="text-white/30 hover:text-red-400 transition-colors p-1">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {visible.length === 0 && <p className="text-white/40 text-sm">No skills match.</p>}
      </div>

      <div className="flex items-center gap-3 mt-6 flex-wrap">
        <input data-testid="new-skill-input" value={newSkill} onChange={(e) => setNewSkill(e.target.value)}
          placeholder={`Add a skill to ${activeCat || "…"}`}
          className="bg-transparent border-b border-white/20 py-2 text-sm outline-none focus:border-[#D4AF37] text-white placeholder:text-white/25 min-w-[220px]" />
        <button data-testid="skill-add" onClick={addSkill}
          className="border border-white/20 text-white/80 px-5 py-2.5 text-xs uppercase tracking-[0.15em] hover:border-white/50 transition-colors">+ Add</button>
        <button data-testid="skills-save" onClick={save} disabled={saving}
          className="bg-[#D4AF37] text-black px-7 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold flex items-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-50 ml-auto">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save ratings"}
        </button>
      </div>
    </div>
  );
}


/* ---------- Fitness testing editor (admin) ---------- */
function FitnessEditor({ clientId, authHeader }) {
  const [metrics, setMetrics] = useState([]);
  const [entries, setEntries] = useState([]);
  const [metric, setMetric] = useState("");
  const [unit, setUnit] = useState("");
  const [value, setValue] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    axios.get(`${API}/fitness-metrics`).then((r) => setMetrics(r.data.metrics || [])).catch(() => {});
    axios.get(`${API}/admin/fitness/${clientId}`, authHeader).then((r) => setEntries(r.data.entries || [])).catch(() => {});
  };
  useEffect(() => { if (clientId) load(); }, [clientId]); // eslint-disable-line

  const pickMetric = (name) => {
    setMetric(name);
    const m = metrics.find((x) => x.name === name);
    if (m) setUnit(m.unit);
  };

  const save = async () => {
    if (!metric.trim() || value === "") { toast.error("Metric and value required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/fitness/${clientId}`, { metric: metric.trim(), unit, value: Number(value), date, note }, authHeader);
      toast.success("Fitness result saved");
      setValue(""); setNote("");
      load();
    } catch {
      toast.error("Could not save result");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/admin/fitness/${id}`, authHeader); setEntries((p) => p.filter((e) => e.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const inputCls = "bg-transparent border-b border-white/20 py-2 text-sm outline-none focus:border-[#D4AF37] text-white placeholder:text-white/25";

  return (
    <div className="mt-12 border-t border-white/10 pt-10" data-testid="fitness-editor">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/40 mb-5">
        <Activity className="w-4 h-4" /> Fitness testing data
      </p>
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1">Metric</label>
          <input list="fitness-metric-list" data-testid="fitness-metric-input" value={metric}
            onChange={(e) => pickMetric(e.target.value)} placeholder="e.g. 10m Sprint" className={`${inputCls} min-w-[200px]`} />
          <datalist id="fitness-metric-list">
            {metrics.map((m) => <option key={m.name} value={m.name} />)}
          </datalist>
        </div>
        <div className="flex flex-col w-20">
          <label className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1">Value</label>
          <input data-testid="fitness-value-input" type="number" step="any" value={value} onChange={(e) => setValue(e.target.value)} className={inputCls} />
        </div>
        <div className="flex flex-col w-20">
          <label className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1">Unit</label>
          <input data-testid="fitness-unit-input" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="s / cm" className={inputCls} />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1">Date</label>
          <input data-testid="fitness-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${inputCls} [color-scheme:dark]`} />
        </div>
        <button data-testid="admin-save-fitness-data-button" onClick={save} disabled={saving}
          className="bg-[#2E6BE6] text-white px-6 py-2.5 text-xs uppercase tracking-[0.15em] font-semibold flex items-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add result"}
        </button>
      </div>

      {entries.length > 0 && (
        <div className="mt-6 space-y-1.5">
          {entries.slice().reverse().map((e) => (
            <div key={e.id} className="flex items-center gap-3 text-sm border border-white/10 px-3 py-2" data-testid={`fitness-entry-${e.id}`}>
              <span className="text-white/50 w-24 shrink-0">{e.date}</span>
              <span className="text-white flex-1">{e.metric}</span>
              <span className="font-display font-bold text-[#2E6BE6]">{e.value} {e.unit}</span>
              <button data-testid={`fitness-delete-${e.id}`} onClick={() => remove(e.id)} className="text-white/30 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

